import { createStyles, makeUiStyles, Theme } from '../styles/styles';
import { fontText, hexToRgbA, StylesIndexInjections } from 'common-react';
import { ButtonSpinnerDimension, ButtonSpinnerMargin } from './tools/Button.const';
import { ButtonVariantModel } from './tools/Button.model';

export interface StyleProps {
  buttonType: 'primary' | 'secondary' | 'tertiary';
  buttonVariant: ButtonVariantModel;
  accent: boolean;
  link: boolean;
  width?: string;
  error?: string;
  hasEnoughSpaceForSpinner?: boolean;
  isLoading?: boolean;
  disableFullWidthOnMobile?: boolean;
}

const getButtonColor = ({ buttonType, accent }: StyleProps, theme: Theme): string => {
  if (buttonType === 'primary') return accent ? theme.color.constants.greys[0] : theme.color.greys[0];
  if (buttonType === 'secondary') return accent ? theme.color.oranges[50] : theme.color.greys[70];
  if (buttonType === 'tertiary') return accent ? theme.color.oranges[50] : theme.color.greys[70];
  return theme.color.greys[0];
};

const getButtonBgColor = ({ buttonType, accent }: StyleProps, theme: Theme): string => {
  if (buttonType === 'primary') return accent ? theme.color.oranges[50] : theme.color.greys[80];
  if (buttonType === 'secondary') return 'transparent';
  return 'transparent';
};

const getBorderColor = ({ buttonType, accent }: StyleProps, theme: Theme): string => {
  if (buttonType === 'primary') return accent ? theme.color.oranges[50] : theme.color.greys[80];
  if (buttonType === 'secondary') return accent ? theme.color.oranges[50] : theme.color.greys[70];
  return 'transparent';
};

const getHoverBgColor = (props: StyleProps, theme: Theme): string =>
  props.accent ? theme.color.oranges[100] : theme.color.greys[82];

const getPressedBgColor = (props: StyleProps, theme: Theme): string =>
  props.accent ? theme.color.oranges[150] : theme.color.greys[84];

const getPressedButtonColor = (props: StyleProps, theme: Theme): string => props.accent ? theme.color.constants.greys[0] : theme.color.greys[0];

const getFocusRingColor = ({ buttonType, accent }: StyleProps, theme: Theme): string => {
  if (buttonType === 'tertiary') return theme.color.greys[70];
  if (accent) return theme.variables.isDarkTheme ? theme.color.constants.greys[0] : theme.color.oranges[50];
  return theme.color.greys[70];
};

export default makeUiStyles((theme: Theme) => createStyles({
  root: {
    position: 'relative',
    transition: 'all 0.15s',
    display: ({ link }: StyleProps) => link ? 'inline-block' : 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    padding: ({ buttonVariant }: StyleProps) => {
      if (buttonVariant === 'large') return '8px 16px';
      if (buttonVariant === 'medium') return '6px 16px';
      return '4px 12px';
    },
    fontSize: ({ buttonVariant }: StyleProps) => {
      if (buttonVariant === 'large') return 19;
      if (buttonVariant === 'medium') return 16;
      return 14;
    },
    lineHeight: ({ buttonVariant }: StyleProps) => {
      if (buttonVariant === 'large') return '28.5px';
      if (buttonVariant === 'medium') return '24px';
      return '21px';
    },
    fontWeight: 700,
    textAlign: 'center',
    textDecoration: 'none',
    cursor: 'pointer',
    color: (props: StyleProps) => getButtonColor(props, theme),
    backgroundColor: (props: StyleProps) => getButtonBgColor(props, theme),
    textTransform: 'none',
    borderRadius: 80,
    boxShadow: 'none',
    border: (props: StyleProps) => `2px solid ${getBorderColor(props, theme)}`,
    minWidth: ({ width }: StyleProps) => width,


    [theme.breakpoints.down('xs')]: {
      width: ({ disableFullWidthOnMobile }: StyleProps) => disableFullWidthOnMobile ? undefined : '100%' },

    '&:hover': {
      color: (props: StyleProps) => props.accent ? theme.color.constants.greys[0] : theme.color.greys[0],
      backgroundColor: (props: StyleProps) => getHoverBgColor(props, theme),
      backgroundImage: 'none',
      border: (props: StyleProps) => {
        if (props.buttonType === 'primary') return `2px solid ${getHoverBgColor(props, theme)}`;
        return '2px solid transparent';
      },
      boxShadow: 'none',
    },

    '&:active': {
      backgroundColor: (props: StyleProps) => getPressedBgColor(props, theme),
      backgroundImage: 'none',
      border: (props: StyleProps) => {
        if (props.buttonType === 'primary') return `2px solid ${getPressedBgColor(props, theme)}`;
        return '2px solid transparent';
      },
      color: (props: StyleProps) => getPressedButtonColor(props, theme),
    },

    '&:hover.Mui-disabled, &:disabled': {
      backgroundColor: (props: StyleProps) => {
        if (props.buttonType === 'tertiary') return 'transparent';
        if (props.buttonType === 'primary') return props.accent
          ? hexToRgbA(theme.color.oranges[50], 0.4)
          : hexToRgbA(theme.color.greys[80], 0.4);
        return 'transparent';
      },
      color: (props: StyleProps) => {
        if (props.buttonType === 'tertiary') return props.accent
          ? hexToRgbA(theme.color.oranges[50], 0.4)
          : hexToRgbA(theme.color.greys[80], 0.4);
        if (props.buttonType === 'secondary') return props.accent
          ? hexToRgbA(theme.color.oranges[50], 0.4)
          : hexToRgbA(theme.color.greys[80], 0.4);
        return theme.variables.isDarkTheme ? hexToRgbA(theme.color.constants.greys[0], 0.4) : hexToRgbA(theme.color.greys[0], 0.4);
      },
      boxShadow: 'none',
      backgroundImage: 'none',
      border: (props: StyleProps) => {
        if (props.buttonType === 'tertiary') return '2px solid transparent';
        if (props.buttonType === 'primary') return `2px solid ${props.accent
          ? hexToRgbA(theme.color.oranges[50], 0)
          : hexToRgbA(theme.color.greys[80], 0)}`;
        return `2px solid ${props.accent
          ? hexToRgbA(theme.color.oranges[50], 0.4)
          : hexToRgbA(theme.color.greys[80], 0.4)}`; // secondary disabled
      },
    },

    '&:focus-visible': {
      outline: 'none',
      boxShadow: (props: StyleProps) => `
        ${theme.variables.isDarkTheme ? 'inset' : ''} 0 0 0 2px ${theme.color.greys[0]},
        ${theme.variables.isDarkTheme ? 'inset' : ''} 0 0 0 4px ${getFocusRingColor(props, theme)}
      `,
    },

    '&:focus-visible:hover': {
      outline: 'none',
      boxShadow: (props: StyleProps) => `
        ${theme.variables.isDarkTheme ? 'inset' : ''} 0 0 0 2px ${theme.color.greys[0]},
        ${theme.variables.isDarkTheme ? 'inset' : ''} 0 0 0 4px ${getFocusRingColor(props, theme)}
      `,
      color: theme.color.greys[0],
      backgroundColor: (props: StyleProps) => getHoverBgColor(props, theme),
      backgroundImage: 'none',
      border: (props: StyleProps) => {
        if (props.buttonType === 'primary') return `2px solid ${getHoverBgColor(props, theme)}`;
        return '2px solid transparent';
      },
    },

    '&:focus-visible:active': {
      outline: 'none',
      boxShadow: (props: StyleProps) => `
        ${theme.variables.isDarkTheme ? 'inset' : ''} 0 0 0 2px ${theme.color.greys[0]},
        ${theme.variables.isDarkTheme ? 'inset' : ''} 0 0 0 4px ${getFocusRingColor(props, theme)}
      `,
      color: (props: StyleProps) => getPressedButtonColor(props, theme),
      backgroundColor: (props: StyleProps) => getPressedBgColor(props, theme),
      backgroundImage: 'none',
      border: (props: StyleProps) => {
        if (props.buttonType === 'primary') return `2px solid ${getPressedBgColor(props, theme)}`;
        return '2px solid transparent';
      },
    },
  },

  linearProgressWrapper: {
    width: '100%'
  },

  linearProgress: {
    backgroundColor: 'inherit',
    position: 'absolute',
    left: '50%',
    width: '86%',
    transform: 'translateX(-50%)',
    height: 2,
    bottom: 0 },

  linearProgressSecondary: {
    backgroundColor: theme.color.greys[50]
  },

  errorWrapper: {
    display: 'flex',
    color: theme.color.reds[100],
    textTransform: 'initial',
    textAlign: 'left',
    font: fontText('normal', theme.fontSize.s, 16),
    position: 'absolute',
    left: 0,
    width: 200,
    bottom: -18 },

  errorIcon: {
    marginLeft: 8
  },

  tooltip: {
    padding: 12,
    font: fontText('normal', theme.fontSize.s, 20),
    color: theme.color.greys[50]
  },

  '@keyframes spinnerSpin': {
    to: { transform: 'rotate(360deg)' }
  },

  spinner: {
    marginLeft: ({ hasEnoughSpaceForSpinner }: StyleProps) => hasEnoughSpaceForSpinner ? ButtonSpinnerMargin : -ButtonSpinnerMargin,
    position: 'absolute',
    top: '50%',
    left: ({ hasEnoughSpaceForSpinner }: StyleProps) => hasEnoughSpaceForSpinner ? '100%' : '50%',
    marginTop: ({ buttonVariant }: StyleProps) => `-${ButtonSpinnerDimension[buttonVariant] / 2}px`,
    flexShrink: 0,
    width: ({ buttonVariant }: StyleProps) => ButtonSpinnerDimension[buttonVariant],
    height: ({ buttonVariant }: StyleProps) => ButtonSpinnerDimension[buttonVariant],
    animation: '$spinnerSpin 0.8s linear infinite',

    '& path': {
      fill: 'currentColor'
    }
  },

  icon: {
    width: 24,
    height: 24,
    flexShrink: 0,
    fill: 'currentColor',
    '& path': {
      fill: 'currentColor'
    },
  },
  iconLeft: {
    opacity: ({ isLoading }: StyleProps) => isLoading ? 0 : 1,
  },

  buttonLabel: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  buttonLabelText: {
    display: 'inherit',
    opacity: ({ hasEnoughSpaceForSpinner }: StyleProps) => hasEnoughSpaceForSpinner ? 1 : 0,
  },
  iconSpinner: {
    animation: '$spinnerSpin 0.8s linear infinite',
  },
}), { name: 'Button', index: StylesIndexInjections.UI_MOLECULES });
